// ---------------------------------------------------------------------------
// Sparkline — compact inline trend, pure data → SVG string
// ---------------------------------------------------------------------------

import { svgAttr, scaleLinear, round2 } from './svg-util.js';
import { chartTitleDesc } from './chart-a11y.js';

/**
 * @typedef {Object} SparklineOpts
 * @property {string} idPrefix
 * @property {string} [title]
 * @property {string} [desc]
 * @property {number} [width]
 * @property {number} [height]
 */

/**
 * Render a minimal trend line (no axes/labels) for inline use in a stat card.
 *
 * @param {number[]} values
 * @param {SparklineOpts} opts
 * @returns {string}
 */
export function sparkline(values, opts) {
  const { idPrefix, title = 'Trend', desc = '', width = 100, height = 28 } = opts;
  const pad = 2;
  const plotW = Math.max(0, width - pad * 2);
  const plotH = Math.max(0, height - pad * 2);
  const safe = (Array.isArray(values) ? values : []).map((v) => Number(v) || 0);
  const max = safe.reduce((m, v) => Math.max(m, v), 0);
  const min = safe.reduce((m, v) => Math.min(m, v), max);

  const { titleId, descId, svgDefs } = chartTitleDesc(
    title,
    desc || `Trend over ${safe.length} points.`,
    idPrefix,
  );

  const n = safe.length;
  const step = n > 1 ? plotW / (n - 1) : 0;
  const pts = safe.map((v, i) => {
    const x = round2(pad + i * step);
    const y = round2(pad + (plotH - scaleLinear(v, min, max, 0, plotH)));
    return `${x},${y}`;
  });

  const polyline = pts.length
    ? `<polyline class="sparkline-line" fill="none" points="${pts.join(' ')}"></polyline>`
    : '';

  return (
    `<svg class="sparkline" viewBox="0 0 ${svgAttr(width)} ${svgAttr(height)}" ` +
    `preserveAspectRatio="none" aria-labelledby="${svgAttr(titleId)} ${svgAttr(descId)}">` +
    svgDefs +
    polyline +
    `</svg>`
  );
}
